import { Outlet, Link, useNavigate, useLocation } from "react-router-dom"
import { useEffect, useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { portalService, type PortalLearner } from "@/services/portal"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { cn } from "@/lib/utils"

const navItems = [
  { path: "/portal/dashboard", label: "Tableau de bord", icon: "home" },
  { path: "/portal/sessions", label: "Mes sessions", icon: "calendar" },
  { path: "/portal/attendance", label: "Émargement", icon: "check-square" },
  { path: "/portal/evaluations", label: "Évaluations", icon: "clipboard-list" },
  { path: "/portal/documents", label: "Documents", icon: "file-text" },
  { path: "/portal/profile", label: "Mon profil", icon: "user" },
]

export function PortalLayout() {
  const navigate = useNavigate()
  const location = useLocation()
  const [learner] = useState<PortalLearner | undefined>(() => {
    return portalService.getCurrentLearner()
  })
  // Le nom vient du serveur : le lien magique ne stocke que l'identifiant.
  const { data: profile } = useQuery({
    queryKey: ["portal-profile"],
    queryFn: () => portalService.getProfile(),
  })

  useEffect(() => {
    if (!portalService.isAuthenticated()) {
      navigate("/portal/login")
    }
  }, [navigate])

  const handleLogout = () => {
    portalService.logout()
  }

  const getInitials = (firstName?: string, lastName?: string): string => {
    const first = firstName?.charAt(0) || "?"
    const last = lastName?.charAt(0) || ""
    return `${first}${last}`.toUpperCase()
  }

  if (!learner) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b border-border/40 bg-card/80 shadow-soft backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex items-center">
              <Link to="/portal/dashboard" className="flex items-center space-x-2.5">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-primary to-emerald-600">
                  <span className="text-sm font-bold text-primary-foreground">Q</span>
                </div>
                <span className="font-semibold tracking-tight text-foreground">Portail Apprenant</span>
              </Link>
            </div>

            {/* Navigation - Desktop */}
            <nav className="hidden md:flex items-center space-x-1">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  data-test={`portal-nav-${item.path.replace("/portal/", "")}`}
                  className={cn(
                    "px-3 py-1.5 text-sm font-medium transition-colors",
                    location.pathname === item.path
                      ? "text-foreground bg-accent rounded-lg"
                      : "text-muted-foreground hover:text-foreground"
                  )}
                >
                  {item.label}
                </Link>
              ))}
            </nav>

            {/* User Menu */}
            <div className="flex items-center">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="relative h-8 w-8 rounded-full" data-test="user-menu-trigger">
                    <Avatar className="h-8 w-8 ring-2 ring-border transition-all hover:ring-primary/40">
                      <AvatarFallback className="bg-primary/10 text-primary font-medium">
                        {profile ? getInitials(profile.first_name, profile.last_name) : getInitials()}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56" align="end" forceMount>
                  <div className="flex flex-col space-y-1 p-2">
                    <p className="text-sm font-medium leading-none" data-test="portal-user-name">
                      {profile ? `${profile.first_name} ${profile.last_name}` : "Apprenant"}
                    </p>
                    <p className="text-xs leading-none text-muted-foreground" data-test="portal-user-email">
                      {profile ? profile.email : ""}
                    </p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600 cursor-pointer" data-test="logout-button">
                    Se déconnecter
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        <div className="md:hidden border-t border-border/40">
          <nav className="flex overflow-x-auto px-4 py-2 space-x-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={cn(
                  "px-3 py-1.5 text-sm font-medium whitespace-nowrap transition-colors",
                  location.pathname === item.path
                    ? "text-foreground bg-accent rounded-lg"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                {item.label}
              </Link>
            ))}
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="border-t border-border/40 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <p className="text-center text-xs text-muted-foreground/50">
            Portail apprenant · Qualneo
          </p>
        </div>
      </footer>
    </div>
  )
}
